import React, { useEffect, useState } from 'react';
import { LayoutDashboardIcon, UsersIcon, ReceiptIcon, WalletIcon, LogOutIcon, UserIcon, ShieldIcon, SettingsIcon, ChevronDownIcon, CalendarIcon, ListTodoIcon, FileBarChartIcon, BatteryChargingIcon, MenuIcon, XIcon, FileTextIcon, BriefcaseIcon, FolderIcon, PlusCircleIcon, ClipboardListIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Customer } from './components/CustomerCard';
import { Task } from './components/TaskCard';
import { Invoice } from './components/InvoiceCard';
import { Payout } from './components/PayoutCard';
import { Earning } from './components/EarningCard';
import { AddCustomerModal } from './components/AddCustomerModal';
import { AddTaskModal } from './components/AddTaskModal';
import { EditCustomerModal } from './components/EditCustomerModal';
import { EditTaskModal } from './components/EditTaskModal';
import { Dashboard } from './pages/Dashboard';
import { CustomerManagement } from './pages/CustomerManagement';
import { CustomerDetail } from './pages/CustomerDetail';
import { Invoices } from './pages/Invoices';
import { Payouts } from './pages/Payouts';
import { Login } from './components/Login';
import { Profile } from './pages/Profile';
import { Users, User as SystemUser, UserRole } from './pages/Users';
import { Calendar } from './pages/Calendar';
import { Report } from './pages/Report';
import { ChargerSettings } from './pages/ChargerSettings';
import { useFirestoreCollection } from './hooks/useFirestore';
import { LandingPage } from './pages/LandingPage';
import { collection, getDocs } from 'firebase/firestore';
import { db } from './firebase';
import { Solar } from './pages/Solar';
import { Regelgrundlag } from './pages/Regelgrundlag';
import { ReportData } from './components/AddReportModal';
import { TaskDetail } from './pages/TaskDetail';
import { AddTaskPage } from './pages/AddTaskPage';
import { ManageTasksPage } from './pages/ManageTasksPage';
type View = 'landing' | 'login' | 'dashboard' | 'customers' | 'tasks' | 'task-detail' | 'report' | 'customer-detail' | 'invoices' | 'payouts' | 'solar' | 'regelgrundlag' | 'profile' | 'users' | 'calendar' | 'charger-settings' | 'add-task-page' | 'manage-tasks-page';
export function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentView, setCurrentView] = useState<View>('landing');
  const [username, setUsername] = useState('');
  const [userRole, setUserRole] = useState<UserRole>('Medarbejder');
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isExpensesOpen, setIsExpensesOpen] = useState(false);
  const [isWorkManagementOpen, setIsWorkManagementOpen] = useState(false);
  const [isDocumentationOpen, setIsDocumentationOpen] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null);
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [previousView, setPreviousView] = useState<View>('tasks');
  const [isCustomerModalOpen, setIsCustomerModalOpen] = useState(false);
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [isEditCustomerModalOpen, setIsEditCustomerModalOpen] = useState(false);
  const [isEditTaskModalOpen, setIsEditTaskModalOpen] = useState(false);
  const [customerToEdit, setCustomerToEdit] = useState<Customer | null>(null);
  const [taskToEdit, setTaskToEdit] = useState<Task | null>(null);
  const [initialCustomerIdForTask, setInitialCustomerIdForTask] = useState<string | undefined>(undefined);
  // Firebase hooks for real-time data syncing
  const {
    data: customers,
    add: addCustomer,
    update: updateCustomer,
    remove: removeCustomer
  } = useFirestoreCollection<Customer>('customers');
  const {
    data: tasks,
    add: addTask,
    update: updateTask,
    remove: removeTask
  } = useFirestoreCollection<Task>('tasks');
  const {
    data: invoices,
    add: addInvoice,
    update: updateInvoice,
    remove: removeInvoice
  } = useFirestoreCollection<Invoice>('invoices');
  const {
    data: payouts,
    add: addPayout,
    update: updatePayout,
    remove: removePayout
  } = useFirestoreCollection<Payout>('payouts');
  const {
    data: earnings,
    add: addEarning,
    update: updateEarning,
    remove: removeEarning
  } = useFirestoreCollection<Earning>('earnings');
  const {
    data: reports,
    add: addReport,
    update: updateReport,
    remove: removeReport
  } = useFirestoreCollection<ReportData>('reports');
  // Check for existing auth on mount
  useEffect(() => {
    const checkAuth = async () => {
      const authStatus = localStorage.getItem('isAuthenticated');
      const storedUsername = localStorage.getItem('username');
      if (authStatus === 'true' && storedUsername) {
        setIsAuthenticated(true);
        setUsername(storedUsername);
        try {
          const usersCollection = collection(db, 'users');
          const usersSnapshot = await getDocs(usersCollection);
          const users: any[] = [];
          usersSnapshot.forEach(doc => {
            users.push({
              id: doc.id,
              ...doc.data()
            });
          });
          const user = users.find(u => u.email === storedUsername);
          if (user) {
            setUserRole(user.role);
          }
        } catch (error) {
          console.error('Error fetching user role:', error);
        }
      }
    };
    checkAuth();
  }, []);
  const handleLogin = (user: string, role: UserRole) => {
    setUsername(user);
    setUserRole(role);
    setIsAuthenticated(true);
    setCurrentView('customers');
  };
  const handleLogout = () => {
    setIsAuthenticated(false);
    setUsername('');
    setCurrentView('landing');
  };
  const handleChangePassword = async (currentPassword: string, newPassword: string): Promise<boolean> => {
    const storedPassword = localStorage.getItem('password');
    if (currentPassword !== storedPassword) {
      return false;
    }
    localStorage.setItem('password', newPassword);
    return true;
  };
  const getNextTaskId = () => {
    if (tasks.length === 0) return 'O001';
    const taskNumbers = tasks.map(t => parseInt(t.taskId?.substring(1) || '0')).filter(num => !isNaN(num));
    if (taskNumbers.length === 0) return 'O001';
    const maxNumber = Math.max(...taskNumbers);
    const nextNumber = maxNumber + 1;
    if (nextNumber > 999) {
      alert('Maksimalt antal opgaver nået (O999). Kontakt support.');
      return `O${nextNumber}`;
    }
    return `O${nextNumber.toString().padStart(3, '0')}`;
  };
  const getNextCustomerId = () => {
    if (customers.length === 0) return 'K001';
    const customerNumbers = customers.map(c => parseInt(c.customerId.substring(1))).filter(num => !isNaN(num));
    if (customerNumbers.length === 0) return 'K001';
    const maxNumber = Math.max(...customerNumbers);
    const nextNumber = maxNumber + 1;
    if (nextNumber > 999) {
      alert('Maksimalt antal kunder nået (K999). Kontakt support.');
      return `K${nextNumber}`;
    }
    return `K${nextNumber.toString().padStart(3, '0')}`;
  };
  const handleAddCustomer = async (customerData: Omit<Customer, 'id'>) => {
    try {
      await addCustomer(customerData);
    } catch (error) {
      console.error('Error adding customer:', error);
      alert('Fejl ved tilføjelse af kunde. Tjek konsollen for detaljer.');
    }
  };
  const handleEditCustomer = (customer: Customer) => {
    setCustomerToEdit(customer);
    setIsEditCustomerModalOpen(true);
  };
  const handleSaveCustomer = async (updatedCustomer: Customer) => {
    try {
      if (!updatedCustomer.id) return;
      await updateCustomer(updatedCustomer.id, updatedCustomer);
      const customerTasks = tasks.filter(t => t.customerId === updatedCustomer.id);
      for (const task of customerTasks) {
        if (task.id) {
          await updateTask(task.id, {
            ...task,
            customerName: updatedCustomer.name
          });
        }
      }
    } catch (error) {
      console.error('Error saving customer:', error);
      alert('Fejl ved opdatering af kunde. Tjek konsollen for detaljer.');
    }
  };
  const handleDeleteCustomer = async (customer: Customer) => {
    const customerTasks = tasks.filter(t => t.customerId === customer.id);
    const confirmMessage = customerTasks.length > 0 ? `Er du sikker på at du vil slette kunden "${customer.name}"?\n\nDenne kunde har ${customerTasks.length} opgave(r) som også vil blive slettet.` : `Er du sikker på at du vil slette kunden "${customer.name}"?`;
    if (!window.confirm(confirmMessage)) return;
    try {
      for (const task of customerTasks) {
        if (task.id) {
          await removeTask(task.id);
        }
      }
      if (customer.id) {
        await removeCustomer(customer.id);
      }
    } catch (error) {
      console.error('Error deleting customer:', error);
      alert('Fejl ved sletning af kunde. Tjek konsollen for detaljer.');
    }
  };
  const handleAddTask = async (taskData: Omit<Task, 'id' | 'customerName'>) => {
    try {
      const customer = customers.find(c => c.id === taskData.customerId);
      if (!customer) {
        alert('Kunde ikke fundet. Vælg venligst en gyldig kunde.');
        return;
      }
      await addTask({
        ...taskData,
        customerName: customer.name
      });
    } catch (error) {
      console.error('Error adding task:', error);
      alert('Fejl ved tilføjelse af opgave. Tjek konsollen for detaljer.');
    }
  };
  // NEW: Handler for opening task modal with pre-selected customer
  const handleOpenAddTaskWithCustomer = (customerId: string) => {
    setInitialCustomerIdForTask(customerId);
    setIsTaskModalOpen(true);
  };
  const handleEditTask = (task: Task) => {
    setTaskToEdit(task);
    setIsEditTaskModalOpen(true);
  };
  const handleSaveTask = async (updatedTask: Task) => {
    try {
      if (!updatedTask.id) return;
      const cleanTask: any = {};
      Object.keys(updatedTask).forEach(key => {
        const value = (updatedTask as any)[key];
        if (value !== undefined) {
          cleanTask[key] = value;
        }
      });
      await updateTask(updatedTask.id, cleanTask);
    } catch (error) {
      console.error('Error saving task:', error);
      alert('Fejl ved opdatering af opgave. Tjek konsollen for detaljer.');
    }
  };
  const handleDeleteTask = async (task: Task) => {
    if (!window.confirm(`Er du sikker på at du vil slette opgaven "${task.title}"?`)) return;
    try {
      if (task.id) {
        await removeTask(task.id);
      }
    } catch (error) {
      console.error('Error deleting task:', error);
      alert('Fejl ved sletning af opgave. Tjek konsollen for detaljer.');
    }
  };
  const handleTaskStatusChange = async (taskId: string, newStatus: Task['status']) => {
    try {
      const task = tasks.find(t => t.id === taskId);
      if (!task) return;
      await updateTask(taskId, {
        ...task,
        status: newStatus
      });
    } catch (error) {
      console.error('Error updating task status:', error);
      alert('Fejl ved opdatering af opgavestatus. Tjek konsollen for detaljer.');
    }
  };
  const handleAddInvoice = async (invoiceData: Omit<Invoice, 'id'>) => {
    try {
      await addInvoice(invoiceData);
    } catch (error) {
      console.error('Error adding invoice:', error);
      alert('Fejl ved tilføjelse af faktura. Tjek konsollen for detaljer.');
    }
  };
  const handleEditInvoice = async (updatedInvoice: Invoice) => {
    try {
      if (!updatedInvoice.id) return;
      await updateInvoice(updatedInvoice.id, updatedInvoice);
    } catch (error) {
      console.error('Error updating invoice:', error);
      alert('Fejl ved opdatering af faktura. Tjek konsollen for detaljer.');
    }
  };
  const handleDeleteInvoice = async (invoice: Invoice) => {
    if (!window.confirm(`Er du sikker på at du vil slette fakturaen fra "${invoice.supplier}"?`)) return;
    try {
      if (invoice.id) {
        await removeInvoice(invoice.id);
      }
    } catch (error) {
      console.error('Error deleting invoice:', error);
      alert('Fejl ved sletning af faktura. Tjek konsollen for detaljer.');
    }
  };
  const handleAddPayout = async (payoutData: Omit<Payout, 'id'>) => {
    try {
      await addPayout(payoutData);
    } catch (error) {
      console.error('Error adding payout:', error);
      alert('Fejl ved tilføjelse af udbetaling. Tjek konsollen for detaljer.');
    }
  };
  const handleEditPayout = async (updatedPayout: Payout) => {
    try {
      if (!updatedPayout.id) return;
      await updatePayout(updatedPayout.id, updatedPayout);
    } catch (error) {
      console.error('Error updating payout:', error);
      alert('Fejl ved opdatering af udbetaling. Tjek konsollen for detaljer.');
    }
  };
  const handleDeletePayout = async (payout: Payout) => {
    if (!window.confirm(`Er du sikker på at du vil slette udbetalingen på ${payout.amount.toLocaleString('da-DK')} kr?`)) return;
    try {
      if (payout.id) {
        await removePayout(payout.id);
      }
    } catch (error) {
      console.error('Error deleting payout:', error);
      alert('Fejl ved sletning af udbetaling. Tjek konsollen for detaljer.');
    }
  };
  const handleAddEarning = async (earningData: Omit<Earning, 'id'>) => {
    try {
      await addEarning(earningData);
    } catch (error) {
      console.error('Error adding earning:', error);
      alert('Fejl ved tilføjelse af indtjening. Tjek konsollen for detaljer.');
    }
  };
  const handleEditEarning = async (updatedEarning: Earning) => {
    try {
      if (!updatedEarning.id) return;
      await updateEarning(updatedEarning.id, updatedEarning);
    } catch (error) {
      console.error('Error updating earning:', error);
      alert('Fejl ved opdatering af indtjening. Tjek konsollen for detaljer.');
    }
  };
  const handleDeleteEarning = async (earning: Earning) => {
    if (!window.confirm(`Er du sikker på at du vil slette indtjeningen på ${earning.amount.toLocaleString('da-DK')} kr?`)) return;
    try {
      if (earning.id) {
        await removeEarning(earning.id);
      }
    } catch (error) {
      console.error('Error deleting earning:', error);
      alert('Fejl ved sletning af indtjening. Tjek konsollen for detaljer.');
    }
  };
  const handleAddReport = async (reportData: Omit<ReportData, 'id' | 'createdAt'>) => {
    try {
      await addReport(reportData as any);
      alert(`Rapport "${reportData.title}" er oprettet og gemt i Firebase!`);
    } catch (error) {
      console.error('Error adding report:', error);
      alert('Fejl ved tilføjelse af rapport. Tjek konsollen for detaljer.');
    }
  };
  const handleUpdateReport = async (reportData: ReportData) => {
    try {
      if (!reportData.id) return;
      await updateReport(reportData.id, reportData as any);
      alert(`Rapport "${reportData.title}" er opdateret!`);
    } catch (error) {
      console.error('Error updating report:', error);
      alert('Fejl ved opdatering af rapport. Tjek konsollen for detaljer.');
    }
  };
  const handleDeleteReport = async (reportId: string) => {
    try {
      await removeReport(reportId);
    } catch (error) {
      console.error('Error deleting report:', error);
      alert('Fejl ved sletning af rapport. Tjek konsollen for detaljer.');
    }
  };
  const handleViewCustomerDetail = (customerId: string) => {
    setSelectedCustomerId(customerId);
    setCurrentView('customer-detail');
  };
  const handleBackFromDetail = () => {
    setSelectedCustomerId(null);
    setCurrentView('customers');
  };
  const handleViewTaskDetail = (taskId: string) => {
    setPreviousView(currentView);
    setSelectedTaskId(taskId);
    setCurrentView('task-detail');
  };
  const handleBackFromTaskDetail = () => {
    setSelectedTaskId(null);
    setCurrentView(previousView === 'task-detail' ? 'tasks' : previousView);
  };
  const isAdmin = userRole === 'Admin';
  const isMedarbejder = userRole === 'Medarbejder';
  const isReadOnly = userRole === 'Read-Only';
  const tabs: any[] = [{
    id: 'dashboard' as View,
    label: 'Overblik',
    icon: LayoutDashboardIcon,
    visible: true
  }];
  const selectedCustomer = selectedCustomerId ? customers.find(c => c.id === selectedCustomerId) : null;
  const selectedTask = selectedTaskId ? tasks.find(t => t.id === selectedTaskId) : null;
  if (!isAuthenticated && currentView === 'landing') {
    return <LandingPage onLoginClick={() => setCurrentView('login')} data-id="element-0" />;
  }
  if (!isAuthenticated && currentView === 'login') {
    return <Login onLogin={handleLogin} data-id="element-1" />;
  }
  return <div className="min-h-screen w-full bg-gradient-to-br from-stone-50 via-teal-50/30 to-stone-50" data-id="element-2">
      {currentView !== 'customer-detail' && currentView !== 'task-detail' && <nav className="bg-white border-b border-stone-200" data-id="element-3">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-4">
            <div className="flex items-center justify-between py-4" data-id="element-5">
              {/* Mobile Menu Button */}
              <button onClick={() => setIsSettingsOpen(!isSettingsOpen)} className="lg:hidden p-2 text-stone-600 hover:text-stone-900 hover:bg-stone-50 rounded-lg transition-colors" aria-label="Menu" data-id="element-6">
                {isSettingsOpen ? <XIcon className="w-6 h-6" data-id="element-7" /> : <MenuIcon className="w-6 h-6" data-id="element-8" />}
              </button>

              {/* Desktop Navigation */}
              <div className="hidden lg:flex gap-1 flex-1" data-id="element-9">
                {/* Overblik Tab */}
                {tabs.map(tab => {
              const Icon = tab.icon;
              const isActive = currentView === tab.id;
              return <button key={tab.id} onClick={() => setCurrentView(tab.id)} className={`relative flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${isActive ? 'text-teal-700' : 'text-stone-600 hover:text-stone-900 hover:bg-stone-50'}`} data-id="element-10">
                      <Icon className="w-5 h-5" data-id="element-11" />
                      {tab.label}
                      {isActive && <motion.div layoutId="activeTab" className="absolute inset-0 bg-teal-50 rounded-lg -z-10" transition={{
                  type: 'spring',
                  duration: 0.5,
                  bounce: 0.2
                }} data-id="element-12" />}
                    </button>;
            })}

                {/* NEW: Arbejdsstyring Dropdown */}
                <div className="relative" data-id="element-13">
                  <button onClick={() => setIsWorkManagementOpen(!isWorkManagementOpen)} className={`relative flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${currentView === 'customers' || currentView === 'calendar' || currentView === 'add-task-page' || currentView === 'manage-tasks-page' ? 'text-teal-700' : 'text-stone-600 hover:text-stone-900 hover:bg-stone-50'}`} data-id="element-14">
                    <BriefcaseIcon className="w-5 h-5" data-id="element-15" />
                    Arbejdsstyring
                    <ChevronDownIcon className={`w-4 h-4 transition-transform ${isWorkManagementOpen ? 'rotate-180' : ''}`} data-id="element-16" />
                    {(currentView === 'customers' || currentView === 'calendar' || currentView === 'add-task-page' || currentView === 'manage-tasks-page') && <motion.div layoutId="activeTab" className="absolute inset-0 bg-teal-50 rounded-lg -z-10" transition={{
                  type: 'spring',
                  duration: 0.5,
                  bounce: 0.2
                }} data-id="element-17" />}
                  </button>

                  <AnimatePresence data-id="element-18">
                    {isWorkManagementOpen && <>
                        <motion.div initial={{
                    opacity: 0
                  }} animate={{
                    opacity: 1
                  }} exit={{
                    opacity: 0
                  }} className="fixed inset-0 z-10" onClick={() => setIsWorkManagementOpen(false)} data-id="element-19" />
                        <motion.div initial={{
                    opacity: 0,
                    y: -10
                  }} animate={{
                    opacity: 1,
                    y: 0
                  }} exit={{
                    opacity: 0,
                    y: -10
                  }} transition={{
                    duration: 0.2
                  }} className="absolute left-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-stone-200 py-2 z-20" data-id="element-20">
                          <button onClick={() => {
                      setCurrentView('customers');
                      setIsWorkManagementOpen(false);
                    }} className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${currentView === 'customers' ? 'bg-teal-50 text-teal-700' : 'text-stone-700 hover:bg-stone-50'}`} data-id="element-21">
                            <UsersIcon className="w-5 h-5" data-id="element-22" />
                            <span className="font-medium" data-id="element-23">Kunde data</span>
                          </button>

                          <button onClick={() => {
                      setCurrentView('calendar');
                      setIsWorkManagementOpen(false);
                    }} className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${currentView === 'calendar' ? 'bg-teal-50 text-teal-700' : 'text-stone-700 hover:bg-stone-50'}`} data-id="element-24">
                            <CalendarIcon className="w-5 h-5" data-id="element-25" />
                            <span className="font-medium" data-id="element-26">Kalender</span>
                          </button>

                          {isAdmin && <>
                              {/* Removed Tilføj Opgaver and Ændre Opgaver buttons from desktop Settings dropdown as per edit intent */}
                            </>}
                        </motion.div>
                      </>}
                  </AnimatePresence>
                </div>

                {/* NEW: Dokumentation Dropdown */}
                <div className="relative" data-id="element-27">
                  <button onClick={() => setIsDocumentationOpen(!isDocumentationOpen)} className={`relative flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${currentView === 'regelgrundlag' || currentView === 'report' ? 'text-teal-700' : 'text-stone-600 hover:text-stone-900 hover:bg-stone-50'}`} data-id="element-28">
                    <FolderIcon className="w-5 h-5" data-id="element-29" />
                    Dokumentation
                    <ChevronDownIcon className={`w-4 h-4 transition-transform ${isDocumentationOpen ? 'rotate-180' : ''}`} data-id="element-30" />
                    {(currentView === 'regelgrundlag' || currentView === 'report') && <motion.div layoutId="activeTab" className="absolute inset-0 bg-teal-50 rounded-lg -z-10" transition={{
                  type: 'spring',
                  duration: 0.5,
                  bounce: 0.2
                }} data-id="element-31" />}
                  </button>

                  <AnimatePresence data-id="element-32">
                    {isDocumentationOpen && <>
                        <motion.div initial={{
                    opacity: 0
                  }} animate={{
                    opacity: 1
                  }} exit={{
                    opacity: 0
                  }} className="fixed inset-0 z-10" onClick={() => setIsDocumentationOpen(false)} data-id="element-33" />
                        <motion.div initial={{
                    opacity: 0,
                    y: -10
                  }} animate={{
                    opacity: 1,
                    y: 0
                  }} exit={{
                    opacity: 0,
                    y: -10
                  }} transition={{
                    duration: 0.2
                  }} className="absolute left-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-stone-200 py-2 z-20" data-id="element-34">
                          <button onClick={() => {
                      setCurrentView('regelgrundlag');
                      setIsDocumentationOpen(false);
                    }} className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${currentView === 'regelgrundlag' ? 'bg-teal-50 text-teal-700' : 'text-stone-700 hover:bg-stone-50'}`} data-id="element-35">
                            <FileTextIcon className="w-5 h-5" data-id="element-36" />
                            <span className="font-medium" data-id="element-37">Regelgrundlag</span>
                          </button>

                          <button onClick={() => {
                      setCurrentView('report');
                      setIsDocumentationOpen(false);
                    }} className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${currentView === 'report' ? 'bg-teal-50 text-teal-700' : 'text-stone-700 hover:bg-stone-50'}`} data-id="element-38">
                            <FileBarChartIcon className="w-5 h-5" data-id="element-39" />
                            <span className="font-medium" data-id="element-40">Rapport</span>
                          </button>
                        </motion.div>
                      </>}
                  </AnimatePresence>
                </div>

                {/* Regnskab Dropdown - Desktop */}
                <div className="relative" data-id="element-41">
                  <button onClick={() => setIsExpensesOpen(!isExpensesOpen)} className={`relative flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${currentView === 'invoices' || currentView === 'payouts' ? 'text-teal-700' : 'text-stone-600 hover:text-stone-900 hover:bg-stone-50'}`} data-id="element-42">
                    <WalletIcon className="w-5 h-5" data-id="element-43" />
                    Regnskab
                    <ChevronDownIcon className={`w-4 h-4 transition-transform ${isExpensesOpen ? 'rotate-180' : ''}`} data-id="element-44" />
                    {(currentView === 'invoices' || currentView === 'payouts') && <motion.div layoutId="activeTab" className="absolute inset-0 bg-teal-50 rounded-lg -z-10" transition={{
                  type: 'spring',
                  duration: 0.5,
                  bounce: 0.2
                }} data-id="element-45" />}
                  </button>

                  <AnimatePresence data-id="element-46">
                    {isExpensesOpen && <>
                        <motion.div initial={{
                    opacity: 0
                  }} animate={{
                    opacity: 1
                  }} exit={{
                    opacity: 0
                  }} className="fixed inset-0 z-10" onClick={() => setIsExpensesOpen(false)} data-id="element-47" />
                        <motion.div initial={{
                    opacity: 0,
                    y: -10
                  }} animate={{
                    opacity: 1,
                    y: 0
                  }} exit={{
                    opacity: 0,
                    y: -10
                  }} transition={{
                    duration: 0.2
                  }} className="absolute left-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-stone-200 py-2 z-20" data-id="element-48">
                          <button onClick={() => {
                      setCurrentView('invoices');
                      setIsExpensesOpen(false);
                    }} className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${currentView === 'invoices' ? 'bg-teal-50 text-teal-700' : 'text-stone-700 hover:bg-stone-50'}`} data-id="element-49">
                            <ReceiptIcon className="w-5 h-5" data-id="element-50" />
                            <span className="font-medium" data-id="element-51">Fakturaer</span>
                          </button>

                          {isAdmin && <button onClick={() => {
                      setCurrentView('payouts');
                      setIsExpensesOpen(false);
                    }} className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${currentView === 'payouts' ? 'bg-teal-50 text-teal-700' : 'text-stone-700 hover:bg-stone-50'}`} data-id="element-52">
                              <WalletIcon className="w-5 h-5" data-id="element-53" />
                              <span className="font-medium" data-id="element-54">
                                Udbetalinger (Intern)
                              </span>
                            </button>}
                        </motion.div>
                      </>}
                  </AnimatePresence>
                </div>
              </div>

              {/* Settings Dropdown - Desktop */}
              <div className="hidden lg:block relative" data-id="element-55">
                <button onClick={() => setIsSettingsOpen(!isSettingsOpen)} className={`relative flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${currentView === 'profile' || currentView === 'users' || currentView === 'charger-settings' ? 'text-teal-700' : 'text-stone-600 hover:text-stone-900 hover:bg-stone-50'}`} data-id="element-56">
                  <SettingsIcon className="w-5 h-5" data-id="element-57" />
                  <ChevronDownIcon className={`w-4 h-4 transition-transform ${isSettingsOpen ? 'rotate-180' : ''}`} data-id="element-58" />
                  {(currentView === 'profile' || currentView === 'users' || currentView === 'charger-settings') && <motion.div layoutId="activeTab" className="absolute inset-0 bg-teal-50 rounded-lg -z-10" transition={{
                type: 'spring',
                duration: 0.5,
                bounce: 0.2
              }} data-id="element-59" />}
                </button>

                <AnimatePresence data-id="element-60">
                  {isSettingsOpen && <>
                      <motion.div initial={{
                  opacity: 0
                }} animate={{
                  opacity: 1
                }} exit={{
                  opacity: 0
                }} className="fixed inset-0 z-10" onClick={() => setIsSettingsOpen(false)} data-id="element-61" />
                      <motion.div initial={{
                  opacity: 0,
                  y: -10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: -10
                }} transition={{
                  duration: 0.2
                }} className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-stone-200 py-2 z-20" data-id="element-62">
                        {isAdmin && <>
                            <button onClick={() => {
                      setCurrentView('users');
                      setIsSettingsOpen(false);
                    }} className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${currentView === 'users' ? 'bg-teal-50 text-teal-700' : 'text-stone-700 hover:bg-stone-50'}`} data-id="element-63">
                              <ShieldIcon className="w-5 h-5" data-id="element-64" />
                              <span className="font-medium" data-id="element-65">Brugere</span>
                            </button>

                            <button onClick={() => {
                      setCurrentView('charger-settings');
                      setIsSettingsOpen(false);
                    }} className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${currentView === 'charger-settings' ? 'bg-teal-50 text-teal-700' : 'text-stone-700 hover:bg-stone-50'}`} data-id="element-66">
                              <BatteryChargingIcon className="w-5 h-5" data-id="element-67" />
                              <span className="font-medium" data-id="element-68">Information</span>
                            </button>
                          </>}

                        <button onClick={() => {
                    setCurrentView('profile');
                    setIsSettingsOpen(false);
                  }} className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${currentView === 'profile' ? 'bg-teal-50 text-teal-700' : 'text-stone-700 hover:bg-stone-50'}`} data-id="element-69">
                          <UserIcon className="w-5 h-5" data-id="element-70" />
                          <span className="font-medium" data-id="element-71">Profil</span>
                        </button>

                        <button onClick={handleLogout} className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-red-600 hover:bg-red-50 transition-colors" data-id="element-72">
                          <LogOutIcon className="w-5 h-5" data-id="element-73" />
                          <span className="font-medium" data-id="element-74">Log ud</span>
                        </button>
                      </motion.div>
                    </>}
                </AnimatePresence>
              </div>

              {/* Mobile Menu - Full Screen Dropdown */}
              <AnimatePresence data-id="element-75">
                {isSettingsOpen && <motion.div initial={{
              opacity: 0,
              x: -300
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -300
            }} transition={{
              type: 'spring',
              damping: 25,
              stiffness: 200
            }} className="lg:hidden fixed inset-0 bg-white z-50 overflow-y-auto" data-id="element-76">
                    <div className="p-4" data-id="element-77">
                      {/* Mobile Menu Header */}
                      <div className="flex items-center justify-between mb-6" data-id="element-78">
                        <h2 className="text-xl font-bold text-stone-900" data-id="element-79">
                          Menu
                        </h2>
                        <button onClick={() => setIsSettingsOpen(false)} className="p-2 text-stone-600 hover:text-stone-900 hover:bg-stone-50 rounded-lg transition-colors" data-id="element-80">
                          <XIcon className="w-6 h-6" data-id="element-81" />
                        </button>
                      </div>

                      {/* Mobile Menu Items */}
                      <div className="space-y-2" data-id="element-82">
                        {/* Overblik */}
                        <button onClick={() => {
                    setCurrentView('dashboard');
                    setIsSettingsOpen(false);
                  }} className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-colors ${currentView === 'dashboard' ? 'bg-teal-50 text-teal-700' : 'text-stone-700 hover:bg-stone-50'}`} data-id="element-83">
                          <LayoutDashboardIcon className="w-5 h-5" data-id="element-84" />
                          Overblik
                        </button>

                        {/* Arbejdsstyring Section */}
                        <div className="pt-2 border-t border-stone-200" data-id="element-85">
                          <p className="px-4 py-2 text-xs font-semibold text-stone-500 uppercase tracking-wider" data-id="element-86">
                            Arbejdsstyring
                          </p>
                          <button onClick={() => {
                      setCurrentView('customers');
                      setIsSettingsOpen(false);
                    }} className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-colors ${currentView === 'customers' ? 'bg-teal-50 text-teal-700' : 'text-stone-700 hover:bg-stone-50'}`} data-id="element-87">
                            <UsersIcon className="w-5 h-5" data-id="element-88" />
                            Kunde data
                          </button>
                          <button onClick={() => {
                      setCurrentView('calendar');
                      setIsSettingsOpen(false);
                    }} className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-colors ${currentView === 'calendar' ? 'bg-teal-50 text-teal-700' : 'text-stone-700 hover:bg-stone-50'}`} data-id="element-89">
                            <CalendarIcon className="w-5 h-5" data-id="element-90" />
                            Kalender
                          </button>
                          {isAdmin && <>
                              {/* Tilføj Opgaver and Ændre Opgaver buttons removed from mobile Settings section as per edit intent */}
                            </>}
                        </div>

                        {/* Dokumentation Section */}
                        <div className="pt-2 border-t border-stone-200" data-id="element-91">
                          <p className="px-4 py-2 text-xs font-semibold text-stone-500 uppercase tracking-wider" data-id="element-92">
                            Dokumentation
                          </p>
                          <button onClick={() => {
                      setCurrentView('regelgrundlag');
                      setIsSettingsOpen(false);
                    }} className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-colors ${currentView === 'regelgrundlag' ? 'bg-teal-50 text-teal-700' : 'text-stone-700 hover:bg-stone-50'}`} data-id="element-93">
                            <FileTextIcon className="w-5 h-5" data-id="element-94" />
                            Regelgrundlag
                          </button>
                          <button onClick={() => {
                      setCurrentView('report');
                      setIsSettingsOpen(false);
                    }} className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-colors ${currentView === 'report' ? 'bg-teal-50 text-teal-700' : 'text-stone-700 hover:bg-stone-50'}`} data-id="element-95">
                            <FileBarChartIcon className="w-5 h-5" data-id="element-96" />
                            Rapport
                          </button>
                        </div>

                        {/* Regnskab Section */}
                        <div className="pt-2 border-t border-stone-200" data-id="element-97">
                          <p className="px-4 py-2 text-xs font-semibold text-stone-500 uppercase tracking-wider" data-id="element-98">
                            Regnskab
                          </p>
                          <button onClick={() => {
                      setCurrentView('invoices');
                      setIsSettingsOpen(false);
                    }} className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-colors ${currentView === 'invoices' ? 'bg-teal-50 text-teal-700' : 'text-stone-700 hover:bg-stone-50'}`} data-id="element-99">
                            <ReceiptIcon className="w-5 h-5" data-id="element-100" />
                            Fakturaer
                          </button>
                          {isAdmin && <button onClick={() => {
                      setCurrentView('payouts');
                      setIsSettingsOpen(false);
                    }} className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-colors ${currentView === 'payouts' ? 'bg-teal-50 text-teal-700' : 'text-stone-700 hover:bg-stone-50'}`} data-id="element-101">
                              <WalletIcon className="w-5 h-5" data-id="element-102" />
                              Udbetalinger (Intern)
                            </button>}
                        </div>

                        {/* Settings Section */}
                        <div className="pt-2 border-t border-stone-200" data-id="element-103">
                          <p className="px-4 py-2 text-xs font-semibold text-stone-500 uppercase tracking-wider" data-id="element-104">
                            Indstillinger
                          </p>
                          {isAdmin && <>
                              <button onClick={() => {
                        setCurrentView('users');
                        setIsSettingsOpen(false);
                      }} className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-colors ${currentView === 'users' ? 'bg-teal-50 text-teal-700' : 'text-stone-700 hover:bg-stone-50'}`} data-id="element-105">
                                <ShieldIcon className="w-5 h-5" data-id="element-106" />
                                Brugere
                              </button>
                              <button onClick={() => {
                        setCurrentView('charger-settings');
                        setIsSettingsOpen(false);
                      }} className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-colors ${currentView === 'charger-settings' ? 'bg-teal-50 text-teal-700' : 'text-stone-700 hover:bg-stone-50'}`} data-id="element-107">
                                <BatteryChargingIcon className="w-5 h-5" data-id="element-108" />
                                Information
                              </button>
                            </>}
                          <button onClick={() => {
                      setCurrentView('profile');
                      setIsSettingsOpen(false);
                    }} className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-colors ${currentView === 'profile' ? 'bg-teal-50 text-teal-700' : 'text-stone-700 hover:bg-stone-50'}`} data-id="element-109">
                            <UserIcon className="w-5 h-5" data-id="element-110" />
                            Profil
                          </button>
                        </div>

                        {/* Log Out */}
                        <div className="pt-2 border-t border-stone-200" data-id="element-111">
                          <button onClick={handleLogout} className="w-full flex items-center gap-3 px-4 py-3 rounded-lg font-medium text-red-600 hover:bg-red-50 transition-colors" data-id="element-112">
                            <LogOutIcon className="w-5 h-5" data-id="element-113" />
                            Log ud
                          </button>
                        </div>
                      </div>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>
          </div>
        </nav>}

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-114">
        {currentView === 'dashboard' && <Dashboard customers={customers} tasks={tasks} invoices={invoices} earnings={earnings} payouts={payouts} onAddTask={handleAddTask} onEditTask={task => handleViewTaskDetail(task.id!)} onViewCustomer={handleViewCustomerDetail} data-id="element-115" />}

        {currentView === 'customers' && <CustomerManagement customers={customers} tasks={tasks} onOpenCustomerModal={() => !isReadOnly && setIsCustomerModalOpen(true)} onOpenTaskModal={() => !isReadOnly && setIsTaskModalOpen(true)} onTaskStatusChange={handleTaskStatusChange} onEditCustomer={customer => !isReadOnly && handleEditCustomer(customer)} onEditTask={task => !isReadOnly && handleViewTaskDetail(task.id!)} onDeleteCustomer={customer => !isReadOnly && handleDeleteCustomer(customer)} onDeleteTask={task => !isReadOnly && handleDeleteTask(task)} onViewCustomerDetail={handleViewCustomerDetail} viewMode="customers" data-id="element-116" />}
        {currentView === 'tasks' && <CustomerManagement customers={customers} tasks={tasks} onOpenCustomerModal={() => !isReadOnly && setIsCustomerModalOpen(true)} onOpenTaskModal={() => !isReadOnly && setIsTaskModalOpen(true)} onTaskStatusChange={handleTaskStatusChange} onEditCustomer={customer => !isReadOnly && handleEditCustomer(customer)} onEditTask={task => !isReadOnly && handleViewTaskDetail(task.id!)} onDeleteCustomer={customer => !isReadOnly && handleDeleteCustomer(customer)} onDeleteTask={task => !isReadOnly && handleDeleteTask(task)} onViewCustomerDetail={handleViewCustomerDetail} viewMode="tasks" data-id="element-117" />}
        {currentView === 'task-detail' && selectedTask && <TaskDetail task={selectedTask} customers={customers} onBack={handleBackFromTaskDetail} onSave={handleSaveTask} isReadOnly={isReadOnly} data-id="element-118" />}
        {currentView === 'report' && <Report tasks={tasks} customers={customers} isReadOnly={isReadOnly} reports={reports} onAddReport={handleAddReport} onUpdateReport={handleUpdateReport} onDeleteReport={handleDeleteReport} data-id="element-119" />}
        {currentView === 'calendar' && <Calendar tasks={tasks} customers={customers} data-id="element-120" />}
        {currentView === 'invoices' && <Invoices invoices={invoices} onAddInvoice={handleAddInvoice} onEditInvoice={handleEditInvoice} onDeleteInvoice={handleDeleteInvoice} canAddInvoice={isAdmin} customers={customers} data-id="element-121" />}
        {currentView === 'payouts' && isAdmin && <Payouts payouts={payouts} earnings={earnings} onAddPayout={handleAddPayout} onEditPayout={handleEditPayout} onDeletePayout={handleDeletePayout} onAddEarning={handleAddEarning} onEditEarning={handleEditEarning} onDeleteEarning={handleDeleteEarning} customers={customers} data-id="element-122" />}
        {currentView === 'solar' && <Solar data-id="element-123" />}
        {currentView === 'regelgrundlag' && <Regelgrundlag data-id="element-124" />}
        {currentView === 'profile' && <Profile username={username} onChangePassword={handleChangePassword} data-id="element-125" />}
        {currentView === 'users' && isAdmin && <Users currentUserEmail={username} data-id="element-126" />}
        {currentView === 'charger-settings' && isAdmin && <ChargerSettings data-id="element-127" />}
        {currentView === 'add-task-page' && isAdmin && <AddTaskPage customers={customers} nextTaskId={getNextTaskId()} onAdd={task => {
        handleAddTask(task);
        setCurrentView('manage-tasks-page');
      }} onBack={() => setCurrentView('tasks')} initialCustomerId={initialCustomerIdForTask} data-id="element-128" />}
        {currentView === 'manage-tasks-page' && isAdmin && <ManageTasksPage tasks={tasks} customers={customers} onEditTask={task => handleViewTaskDetail(task.id!)} onDeleteTask={handleDeleteTask} data-id="element-129" />}
        {currentView === 'customer-detail' && selectedCustomer && <CustomerDetail customer={selectedCustomer} tasks={tasks} onBack={handleBackFromDetail} onEdit={customer => !isReadOnly && handleEditCustomer(customer)} onEditTask={task => handleViewTaskDetail(task.id!)} onDeleteTask={handleDeleteTask} onAddTask={handleOpenAddTaskWithCustomer} data-id="element-130" />}
      </main>

      {!isReadOnly && <>
          <AddCustomerModal isOpen={isCustomerModalOpen} onClose={() => setIsCustomerModalOpen(false)} onAdd={handleAddCustomer} nextCustomerId={getNextCustomerId()} data-id="element-131" />
          <AddTaskModal isOpen={isTaskModalOpen} onClose={() => {
        setIsTaskModalOpen(false);
        setInitialCustomerIdForTask(undefined);
      }} onAdd={handleAddTask} customers={customers} nextTaskId={getNextTaskId()} initialCustomerId={initialCustomerIdForTask} data-id="element-132" />
          <EditCustomerModal isOpen={isEditCustomerModalOpen} onClose={() => {
        setIsEditCustomerModalOpen(false);
        setCustomerToEdit(null);
      }} onSave={handleSaveCustomer} customer={customerToEdit} data-id="element-133" />
          <EditTaskModal isOpen={isEditTaskModalOpen} onClose={() => {
        setIsEditTaskModalOpen(false);
        setTaskToEdit(null);
      }} onSave={handleSaveTask} task={taskToEdit} customers={customers} data-id="element-134" />
        </>}
    </div>;
}